// Job devices (card 540f21a4): the laptops, iPads and tablets working at one
// event, each with one job. The page lists them; "Add a device" opens a modal
// to make a link to text or email to the device's user, or to pair one by the
// code it shows (it opens feis.link/device), then pick its job and give it a
// name ("Tab laptop 2").
// Every device stops at midnight after the event, or when stopped here. Jobs
// and what each one includes are set by Feis.link (the admin Roles screen).
//
// The admin app carries a copy (back link, link origin and the Oireachtas
// stage-tablet option differ): keep the two in step.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderOrg from '../Headers/HeaderOrg';
import Loader from '../../Components/Loader/Loader';
import QrCode from '../../Components/QrCode/QrCode';
import { Msr, useMaterialSymbols } from '../../Teacher/Oireachtas/components';
import { listDevices, makeDeviceLink, makeStageLink, pairDevice, revokeDevice, revokeStageTablet, updateDevice } from './jobDevicesApi';
import '../../Teacher/Oireachtas/oireachtas.css';
import '../Oireachtas/organizerOireachtas.css';
import './jobDevices.css';

const BACK = (kind, eventId) => `/organizer-manage-feis?id=${eventId}&Manage=feis`;
const STAGE = 'stage';

// what Tab can reach inside the modal
const focusables = (root) =>
  [...root.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')].filter(
    (el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true'
  );

const messageOf = (err) => err?.messages || err?.message || 'Something went wrong. Please try again.';
const when = (d) => {
  if (!d) return '';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
// "482913" → "482-913" while typing (codes are 6 digits)
export const formatCodeInput = (v) => {
  const s = String(v || '').replace(/[^0-9]/g, '').slice(0, 6);
  return s.length > 3 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
};
// A copied link opens the device page with the device's key after the #.
export const deviceLinkUrl = (token, origin = window.location.origin) => `${origin}/device#t=${token}`;
// A stage check-in tablet's link opens the website's stage page.
export const stageTabletUrl = (token, origin = window.location.origin) => `${origin}/stage#t=${token}`;

const JobDevices = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get('id');
  const kind = params.get('kind') === 'oireachtas' ? 'oireachtas' : 'feis';
  // a code from a device's QR code (the /pair page): opens Add a device with it
  const codeFromQr = formatCodeInput(params.get('code'));

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [confirmStop, setConfirmStop] = useState('');
  // the Add a device modal: null (closed) | 'choose' | 'code' | 'link' | 'qr'
  const [adding, setAdding] = useState(null);
  const [addError, setAddError] = useState('');
  const [code, setCode] = useState('');
  const [job, setJob] = useState('');
  const [label, setLabel] = useState('');
  const [paired, setPaired] = useState('');
  const [made, setMade] = useState(null);
  const codeRef = useRef(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await listDevices(kind, eventId));
    } catch (e) {
      setError(messageOf(e));
    }
  }, [kind, eventId]);

  useEffect(() => {
    if (eventId) load();
  }, [eventId, load]);

  // a stage check-in tablet is for the whole event (Devin 2026-10-25): it
  // picks its stage and works today, so it needs a saved schedule with stages
  const hasStages = useMemo(() => (data?.stage?.days || []).some((d) => (d.stages || []).length > 0), [data]);

  const closed = data?.pairing?.closedReason || '';
  const codeReady = code.replace('-', '').length === 6;
  const jobReady = !!job;
  const canPair = codeReady && jobReady && !busy;

  const resetForm = () => {
    setAddError('');
    setCode('');
    setJob('');
    setLabel('');
    setPaired('');
    setMade(null);
  };
  const openAdd = () => {
    if (busy) return;
    // where focus goes back to (by the time the modal's effect runs, its
    // first button already has focus)
    openerRef.current = document.activeElement;
    resetForm();
    setAdding('choose');
  };
  const choose = (way) => {
    resetForm();
    setAdding(way);
  };
  const closeAdd = useCallback(() => {
    if (busy === 'pair' || busy === 'link') return;
    setAdding(null);
  }, [busy]);

  // arrived from a scanned device QR code: open Pairing code with the code in,
  // once the event is loaded and open for pairing; the code leaves the address
  const qrHandled = useRef(false);
  useEffect(() => {
    if (qrHandled.current || !data || codeFromQr.replace('-', '').length !== 6) return;
    qrHandled.current = true;
    const rest = new URLSearchParams(location.search);
    rest.delete('code');
    navigate(`${location.pathname}?${rest.toString()}`, { replace: true });
    if (closed) return;
    resetForm();
    setCode(codeFromQr);
    setAdding('code');
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // The modal holds keyboard focus (admin MC review 19252b8f R1-F2): the page
  // behind is inert, Tab stays inside, and focus goes back where it was.
  const modalRef = useRef(null);
  const openerRef = useRef(null);
  const open = !!adding;
  useEffect(() => {
    const modal = modalRef.current;
    const scrim = modal && modal.parentElement;
    const page = scrim && scrim.parentElement;
    if (!open || !page) return undefined;
    const opener = openerRef.current;
    const others = (parent, keep) => (parent ? [...parent.children].filter((el) => el !== keep) : []);
    const behind = [...others(page, scrim), ...others(page.parentElement, page)].map((el) => ({
      el,
      inert: el.getAttribute('inert'),
      hidden: el.getAttribute('aria-hidden'),
    }));
    behind.forEach(({ el }) => {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });
    const onTab = (e) => {
      if (e.key !== 'Tab') return;
      const all = focusables(modal);
      if (!all.length) return;
      const head = all[0];
      const tail = all[all.length - 1];
      if (e.shiftKey && (document.activeElement === head || !modal.contains(document.activeElement))) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && (document.activeElement === tail || !modal.contains(document.activeElement))) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onTab);
    return () => {
      document.removeEventListener('keydown', onTab);
      behind.forEach(({ el, inert, hidden }) => {
        if (inert == null) el.removeAttribute('inert');
        else el.setAttribute('inert', inert);
        if (hidden == null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', hidden);
      });
      if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!adding) return undefined;
    const onKey = (e) => e.key === 'Escape' && closeAdd();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [adding, closeAdd]);

  const pair = async (e) => {
    e.preventDefault();
    if (!canPair) return;
    setBusy('pair');
    setAddError('');
    try {
      const body = { code, label: label.trim() };
      if (job === STAGE) body.stage = { allDays: true };
      else body.roleId = job;
      const res = await pairDevice(kind, eventId, body);
      const name = res.kind === 'stage' ? `${res.tablet.label || 'Tablet'} (stage check-in)` : `${res.device.label || 'Device'} (${res.device.role?.name})`;
      setPaired(`Paired: ${name}. The device opens its job by itself.`);
      await load();
    } catch (err) {
      setAddError(messageOf(err));
    } finally {
      setBusy('');
    }
  };

  const pairAnother = () => {
    setCode('');
    setLabel('');
    setPaired('');
    setAddError('');
    setTimeout(() => codeRef.current && codeRef.current.focus(), 0);
  };

  const changeJob = async (d, roleId) => {
    setBusy(d._id);
    setError('');
    try {
      await updateDevice(kind, eventId, d._id, { roleId });
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy('');
    }
  };

  // stage check-in tablets are stage links; everything else a job device
  const stop = async (d, tablet = false) => {
    setBusy(d._id);
    setError('');
    try {
      if (tablet) await revokeStageTablet(eventId, d._id);
      else await revokeDevice(kind, eventId, d._id);
      setConfirmStop('');
      await load();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy('');
    }
  };

  const makeLink = async (e) => {
    e.preventDefault();
    if (!jobReady || busy) return;
    setBusy('link');
    setAddError('');
    try {
      if (job === STAGE) {
        const res = await makeStageLink(eventId, { allDays: true, label: label.trim() });
        setMade({ url: stageTabletUrl(res.token), copied: false, name: res.link?.label || 'the stage tablet' });
        await load();
        return;
      }
      const res = await makeDeviceLink(kind, eventId, { roleId: job, label: label.trim() });
      setMade({ url: deviceLinkUrl(res.token), copied: false, name: res.device.label || res.device.role?.name });
      await load();
    } catch (err) {
      setAddError(messageOf(err));
    } finally {
      setBusy('');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.url);
      setMade({ ...made, copied: true });
    } catch (e) {
      // select it so it can be copied by hand
    }
  };
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const share = async () => {
    try {
      await navigator.share({ title: `Feis.link device: ${made.name || 'event device'}`, url: made.url });
    } catch (e) {
      // dismissed: the link is still there to copy
    }
  };

  const roles = data?.roles || [];
  const deviceCount = (data?.devices?.length || 0) + (data?.stage?.tablets?.length || 0);

  const jobSelect = () => (
    <label className="jd-field">
      <span>Job</span>
      <select value={job} onChange={(e) => setJob(e.target.value)} aria-label="Job for the device">
        <option value="">Pick a job</option>
        {roles.map((r) => (
          <option key={r._id} value={r._id}>
            {r.name}
          </option>
        ))}
        {kind === 'oireachtas' && hasStages ? <option value={STAGE}>Stage check-in tablet</option> : null}
      </select>
    </label>
  );
  // a stage tablet picks its stage itself and works today, every event day
  const stageNote = () => (job === STAGE ? <p className="jd-note">The tablet picks its stage and works each day of the event.</p> : null);
  const nameInput = (placeholder) => (
    <label className="jd-field">
      <span>Name (optional)</span>
      <input value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} placeholder={placeholder} />
    </label>
  );
  const addErrorBanner = addError ? (
    <div className="jd-banner err" role="alert">
      <Msr>error</Msr>
      {addError}
    </div>
  ) : null;

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page jd-page">
        {!data && !error && eventId && <Loader />}
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate(BACK(kind, eventId))}>
            <Msr>arrow_back</Msr>Manage event
          </button>
          <div className="ph">
            <div>
              <h1>Event devices</h1>
              <div className="sub">
                {data?.event?.name}
                {data?.pairing?.endsAt ? <span>· devices stop {when(data.pairing.endsAt)}</span> : null}
              </div>
            </div>
            <button type="button" className="op-btn jd-primary op-ph-act" onClick={openAdd} disabled={!data || !!closed}>
              <Msr>add</Msr>Add a device
            </button>
          </div>

          {error && (
            <div className="jd-banner err" role="alert">
              <Msr>error</Msr>
              {error}
            </div>
          )}
          {closed && (
            <div className="jd-banner" role="status">
              <Msr>schedule</Msr>
              {closed}
            </div>
          )}

          <section className="jd-card" aria-labelledby="jd-list-h">
            <h2 id="jd-list-h">Devices ({deviceCount})</h2>
            {data && !deviceCount ? <p className="jd-help">No devices yet. Use Add a device to pair the event's laptops, iPads and tablets.</p> : null}
            <div className="jd-list">
              {(data?.devices || []).map((d) => (
                <div className="jd-row" key={d._id}>
                  <div className="jd-who">
                    <div className="jd-name">{d.label || 'Unnamed device'}</div>
                    <div className="jd-meta">
                      {d.lastUsedAt ? `Last used ${when(d.lastUsedAt)}` : 'Not used yet'}
                      {d.createdBy ? ` · paired by ${d.createdBy}` : ''}
                    </div>
                  </div>
                  <select
                    className="jd-role"
                    value={d.role?._id || ''}
                    disabled={busy === d._id}
                    onChange={(e) => changeJob(d, e.target.value)}
                    aria-label={`Job for ${d.label || 'device'}`}
                  >
                    {!d.role && <option value="">(no job)</option>}
                    {roles.map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  {confirmStop === d._id ? (
                    <span className="jd-confirm">
                      <button type="button" className="op-btn sm danger" disabled={busy === d._id} onClick={() => stop(d)}>
                        Stop
                      </button>
                      <button type="button" className="op-btn sm" onClick={() => setConfirmStop('')}>
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button type="button" className="op-btn sm" onClick={() => setConfirmStop(d._id)}>
                      Stop device
                    </button>
                  )}
                </div>
              ))}
              {(data?.stage?.tablets || []).map((t) => (
                <div className="jd-row" key={t._id}>
                  <div className="jd-who">
                    <div className="jd-name">{t.label || t.stageName || (t.allDays ? 'Stage tablet' : `Stage ${t.stage}`)}</div>
                    <div className="jd-meta">
                      {t.allDays ? 'Stage check-in · whole event' : `Stage check-in · ${t.day} · ${t.stageName}`}
                      {t.lastUsedAt ? ` · last used ${when(t.lastUsedAt)}` : ''}
                    </div>
                  </div>
                  <span />
                  {confirmStop === t._id ? (
                    <span className="jd-confirm">
                      <button type="button" className="op-btn sm danger" disabled={busy === t._id} onClick={() => stop(t, true)}>
                        Stop
                      </button>
                      <button type="button" className="op-btn sm" onClick={() => setConfirmStop('')}>
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button type="button" className="op-btn sm" onClick={() => setConfirmStop(t._id)}>
                      Stop device
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        {adding && (
          <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && closeAdd()}>
            <div className="omodal jd-modal" role="dialog" aria-modal="true" aria-labelledby="jd-add-h" ref={modalRef}>
              <button type="button" className="jd-x" aria-label="Close" onClick={closeAdd} disabled={busy === 'pair' || busy === 'link'}>
                <Msr>close</Msr>
              </button>
              {adding === 'choose' && (
                <>
                  <h3 id="jd-add-h">Add a device</h3>
                  <p>There are three ways to add a device.</p>
                  <div className="jd-choices">
                    <button type="button" className="jd-choice" onClick={() => choose('link')} autoFocus>
                      <Msr>send</Msr>
                      <span className="jd-choice-txt">
                        <b>Send a link</b>
                        <span className="jd-choice-d">
                          Create a link and text or email it to the person using the device. Opening it on that device sets it up.
                        </span>
                      </span>
                    </button>
                    <button type="button" className="jd-choice" onClick={() => choose('code')}>
                      <Msr>dialpad</Msr>
                      <span className="jd-choice-txt">
                        <b>Pairing code</b>
                        <span className="jd-choice-d">
                          For a device that can't get texts or email. Open feis.link/device on it to show a code, then type the code here.
                        </span>
                      </span>
                    </button>
                    <button type="button" className="jd-choice" onClick={() => choose('qr')}>
                      <Msr>qr_code_2</Msr>
                      <span className="jd-choice-txt">
                        <b>QR code</b>
                        <span className="jd-choice-d">
                          Show a QR code on this screen and scan it with the device's camera. For tablets and phones that read QR codes.
                        </span>
                      </span>
                    </button>
                  </div>
                  <div className="modal-act">
                    <button type="button" className="op-btn" onClick={closeAdd}>
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {adding === 'code' && (
                <>
                  <h3 id="jd-add-h">Pair with a code</h3>
                  {paired ? (
                    <>
                      <p className="jd-ok" role="status">
                        <Msr>check_circle</Msr>
                        {paired}
                      </p>
                      <div className="modal-act">
                        <button type="button" className="op-btn" onClick={pairAnother}>
                          Pair another
                        </button>
                        <button type="button" className="op-btn jd-primary" onClick={closeAdd} autoFocus>
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={pair}>
                      <p>
                        On the laptop, iPad or tablet, open <b>feis.link/device</b>. Type the code it shows, pick its job, and give it a name.
                      </p>
                      {addErrorBanner}
                      <div className="jd-stack">
                        <label className="jd-field">
                          <span>Code</span>
                          <input
                            ref={codeRef}
                            className="jd-code"
                            value={code}
                            onChange={(e) => setCode(formatCodeInput(e.target.value))}
                            placeholder="482-913"
                            autoComplete="off"
                            spellCheck={false}
                            inputMode="numeric"
                            aria-label="Code the device shows"
                            autoFocus
                          />
                        </label>
                        {jobSelect()}
                        {stageNote()}
                        {nameInput('Tab laptop 2')}
                      </div>
                      <div className="modal-act">
                        <button type="button" className="op-btn" onClick={() => setAdding('choose')} disabled={busy === 'pair'}>
                          Back
                        </button>
                        <button type="submit" className="op-btn jd-primary" disabled={!canPair}>
                          <Msr>link</Msr>Pair
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}

              {adding === 'link' && (
                <>
                  <h3 id="jd-add-h">Send a link</h3>
                  {made ? (
                    <>
                      <p>
                        Text or email this link to the person using {made.name ? <b>{made.name}</b> : 'the device'}, to open on that device. It is shown only
                        once, and anyone with it can use the job, so send it only to them.
                      </p>
                      <div className="jd-made">
                        <input readOnly value={made.url} onFocus={(e) => e.target.select()} aria-label="Device link" />
                      </div>
                      <div className="modal-act">
                        {canShare && (
                          <button type="button" className="op-btn" onClick={share}>
                            <Msr>ios_share</Msr>Share
                          </button>
                        )}
                        <button type="button" className="op-btn" onClick={copy}>
                          <Msr>content_copy</Msr>
                          {made.copied ? 'Copied' : 'Copy link'}
                        </button>
                        <button type="button" className="op-btn jd-primary" onClick={closeAdd}>
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={makeLink}>
                      <p>Pick the device's job and give it a name. You'll get a link to text or email to the person using it.</p>
                      {addErrorBanner}
                      <div className="jd-stack">
                        {jobSelect()}
                        {stageNote()}
                        {nameInput('Tab laptop 3')}
                      </div>
                      <div className="modal-act">
                        <button type="button" className="op-btn" onClick={() => setAdding('choose')} disabled={busy === 'link'}>
                          Back
                        </button>
                        <button type="submit" className="op-btn jd-primary" disabled={!jobReady || !!busy}>
                          Make link
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}

              {adding === 'qr' && (
                <>
                  <h3 id="jd-add-h">Scan a QR code</h3>
                  {made ? (
                    <>
                      <p>
                        Point the camera of {made.name ? <b>{made.name}</b> : 'the device'} at this code and open the link it finds. The code is shown only once;
                        anyone who scans it can use the job.
                      </p>
                      <div className="jd-qr-big">
                        <QrCode value={made.url} size={260} label="QR code for the device to scan" />
                      </div>
                      <div className="modal-act">
                        <button type="button" className="op-btn" onClick={copy}>
                          <Msr>content_copy</Msr>
                          {made.copied ? 'Copied' : 'Copy link instead'}
                        </button>
                        <button type="button" className="op-btn jd-primary" onClick={closeAdd}>
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={makeLink}>
                      <p>Pick the device's job and give it a name. You'll get a QR code to scan with the device's camera.</p>
                      {addErrorBanner}
                      <div className="jd-stack">
                        {jobSelect()}
                        {stageNote()}
                        {nameInput('Side stage tablet')}
                      </div>
                      <div className="modal-act">
                        <button type="button" className="op-btn" onClick={() => setAdding('choose')} disabled={busy === 'link'}>
                          Back
                        </button>
                        <button type="submit" className="op-btn jd-primary" disabled={!jobReady || !!busy}>
                          <Msr>qr_code_2</Msr>Make QR code
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default JobDevices;
